import type { Access } from "./access.ts";

/** Points at another lifecycle (in another resource's access map) that must
 * also be provisioned as a prerequisite of the access that declares
 * this delegate.
 *
 * The most common way in which this is used is by static declaration
 * on another access type's Access() definition.
 *
 * @example
 * ```
 * export const DbAccesses = {
 *   role: Access({
 *     delegation: {
 *       "aws-rds": Delegate(...),
 *       ...
 *     },
 *     parameters: { ... },
 *     generated: { ... },
 *   })
 * }
 * ```
 */
export type Delegate<
  Accesses extends Record<string, Access<any, any, any>>,
  K extends keyof Accesses,
> = {
  readonly resource: string;
  readonly key: K;
  readonly accesses: Accesses;
};

export const Delegate = <
  Accesses extends Record<string, Access<any, any, any>>,
  K extends keyof Accesses,
>(
  delegate: Delegate<Accesses, K>
): Delegate<Accesses, K> => delegate;
